import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Loader, Select } from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import {
	collectStats,
	combineBatStats,
	combineBowlStats,
	combineFieldStats,
	getSeasons,
	getTeamGradeIds
} from "../../cricket-api/combined-api";
import {
	PlayerBatStats,
	PlayerBowlStats,
	PlayerFieldStats,
	Season,
	Team
} from "../../schema/schema";
import { formatBallsAsOvers } from "../../utils/cricketFormat";

type MetricKey =
	| "mostRuns"
	| "topAverage"
	| "mostFours"
	| "bestBoundaryRate"
	| "bestBoundaryPercentage"
	| "mostBallsFaced"
	| "mostWickets"
	| "bestEconomy"
	| "bestBowlingStrikeRate"
	| "mostBallsBowled"
	| "mostSixes"
	| "mostFifties"
	| "mostHundreds"
	| "bestStrikeRate"
	| "bestBowlAverage"
	| "mostCatches"
	| "mostMaidens"
	| "mostRunOuts"
	| "mostStumpings"
	| "mostGamesPlayed";

type LeaderCard = {
	key: MetricKey;
	title: string;
	player: string;
	value: string;
	description: string;
	secondary: Array<{ rank: number; player: string; value: string; description: string }>;
	accent: string;
	group: "batting" | "bowling" | "fielding";
};

const selectClassNames = {
	input: "bg-slate-900/70 border-slate-600 text-slate-100",
	label: "text-slate-300",
	dropdown: "bg-white border border-slate-300 text-slate-900",
	option:
		"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
};

const SeasonDashboard: FC<{ orgId: string }> = ({ orgId }) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const seasonParam = searchParams.get("season") || "";
	const gradeParam = searchParams.get("grade") || "all";

	const [seasons, setSeasons] = useState<Season[]>([]);
	const [selectedSeason, setSelectedSeason] = useState<string>(seasonParam);
	const [grades, setGrades] = useState<Team[]>([]);
	const [selectedGrade, setSelectedGrade] = useState<string>(gradeParam);

	const [batStats, setBatStats] = useState<PlayerBatStats[]>([]);
	const [bowlStats, setBowlStats] = useState<PlayerBowlStats[]>([]);
	const [fieldStats, setFieldStats] = useState<PlayerFieldStats[]>([]);
	const [expandedCards, setExpandedCards] = useState<Partial<Record<MetricKey, boolean>>>({});
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");

	const seasonName = seasons.find(season => season.Id === selectedSeason)?.Name || "Season";
	const gradeName =
		selectedGrade === "all"
			? "All Grades"
			: grades.find(grade => grade.Id === selectedGrade)?.Grade?.Name || "Selected Grade";

	const toBattingAverage = (batter: PlayerBatStats) => {
		const outs = batter.Statistics.BattingInnings - batter.Statistics.BattingNotOuts;
		if (outs <= 0) {
			return null;
		}
		return batter.Statistics.BattingAggregate / outs;
	};

	const toStrikeRate = (batter: PlayerBatStats) => {
		if (batter.Statistics.BattingBallsFaced <= 0) {
			return null;
		}
		return (batter.Statistics.BattingAggregate / batter.Statistics.BattingBallsFaced) * 100;
	};

	const toBoundaryRate = (batter: PlayerBatStats) => {
		const boundaries = batter.Statistics.BattingFours + batter.Statistics.BattingSixes;
		if (boundaries <= 0 || batter.Statistics.BattingBallsFaced <= 0) {
			return null;
		}
		return batter.Statistics.BattingBallsFaced / boundaries;
	};

	const toBoundaryPercentage = (batter: PlayerBatStats) => {
		if (batter.Statistics.BattingAggregate <= 0) {
			return null;
		}
		const boundaryRuns = batter.Statistics.BattingFours * 4 + batter.Statistics.BattingSixes * 6;
		return (boundaryRuns / batter.Statistics.BattingAggregate) * 100;
	};

	const toEconomy = (bowler: PlayerBowlStats) => {
		if (bowler.Statistics.BowlingBalls <= 0) {
			return null;
		}
		return (bowler.Statistics.BowlingRuns / bowler.Statistics.BowlingBalls) * 6;
	};

	const toBowlingAverage = (bowler: PlayerBowlStats) => {
		if (bowler.Statistics.BowlingWickets <= 0) {
			return null;
		}
		return bowler.Statistics.BowlingRuns / bowler.Statistics.BowlingWickets;
	};

	const toBowlingStrikeRate = (bowler: PlayerBowlStats) => {
		if (bowler.Statistics.BowlingWickets <= 0) {
			return null;
		}
		return bowler.Statistics.BowlingBalls / bowler.Statistics.BowlingWickets;
	};

	const formatDecimal = (value: number) => Math.round(value * 100) / 100;

	const splitStatValue = (value: string) => {
		const matchedValue = value.trim().match(/^([0-9]+(?:\.[0-9]+)?)\s*(.*)$/);
		if (!matchedValue) {
			return { primary: value, suffix: "" };
		}

		return {
			primary: matchedValue[1],
			suffix: matchedValue[2]
		};
	};

	const toggleCardExpanded = useCallback((key: MetricKey) => {
		setExpandedCards(prev => ({
			...prev,
			[key]: !prev[key]
		}));
	}, []);

	const updateDashboardQuery = useCallback(
		(nextSeason: string, nextGrade: string) => {
			setSearchParams(
				prev => {
					const next = new URLSearchParams(prev);

					if (nextSeason) {
						next.set("season", nextSeason);
					} else {
						next.delete("season");
					}

					if (nextGrade && nextGrade !== "all") {
						next.set("grade", nextGrade);
					} else {
						next.delete("grade");
					}

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const formatPlayerName = (name?: string) => {
		if (!name) {
			return "N/A";
		}

		const cleanedName = name.trim();
		if (!cleanedName) {
			return "N/A";
		}

		if (!cleanedName.includes(",")) {
			return cleanedName;
		}

		const [lastName, firstName] = cleanedName.split(",").map(part => part.trim());
		if (!firstName) {
			return cleanedName;
		}

		return `${firstName} ${lastName}`.replace(/\s+/g, " ").trim();
	};

	useEffect(() => {
		let cancelled = false;

		(async () => {
			setLoading(true);
			setError("");
			try {
				const seasonsResponse = await getSeasons(orgId);
				if (cancelled) {
					return;
				}
				setSeasons(seasonsResponse);

				const defaultSeasonId =
					seasonsResponse.find(season => season.IsCurrentSeason)?.Id ||
					seasonsResponse[0]?.Id ||
					"";
				const nextSeasonId = seasonsResponse.some(season => season.Id === seasonParam)
					? seasonParam
					: defaultSeasonId;
				const nextGrade = gradeParam || "all";

				setSelectedSeason(nextSeasonId);
				setSelectedGrade(nextGrade);
				updateDashboardQuery(nextSeasonId, nextGrade);
			} catch {
				if (!cancelled) {
					setError("Could not load seasons for this club.");
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [orgId, gradeParam, seasonParam, updateDashboardQuery]);

	useEffect(() => {
		if (!selectedSeason) {
			return;
		}

		let cancelled = false;

		(async () => {
			setLoading(true);
			setError("");
			try {
				const teams = await getTeamGradeIds(orgId, selectedSeason);
				if (cancelled) {
					return;
				}
				setGrades(teams);

				const hasSelectedGrade = teams.some(team => team.Id === selectedGrade);
				const nextGrade = selectedGrade === "all" || hasSelectedGrade ? selectedGrade : "all";

				if (nextGrade !== selectedGrade) {
					setSelectedGrade(nextGrade);
				}

				updateDashboardQuery(selectedSeason, nextGrade);
				const teamStats = await collectStats(teams);
				if (cancelled) {
					return;
				}

				if (nextGrade !== "all") {
					const selectedTeam = teamStats.find(team => team.teamName === nextGrade);
					setBatStats(selectedTeam?.batStats || []);
					setBowlStats(selectedTeam?.bowlStats || []);
					setFieldStats(selectedTeam?.fieldStats || []);
				} else {
					setBatStats(combineBatStats(teamStats));
					setBowlStats(combineBowlStats(teamStats));
					setFieldStats(combineFieldStats(teamStats));
				}
			} catch {
				if (!cancelled) {
					setError("Could not load dashboard highlights for this selection.");
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [selectedSeason, selectedGrade, orgId, updateDashboardQuery]);

	const allCards = useMemo<LeaderCard[]>(() => {
		type RankedPlayer = { player: string; value: string; raw: number; description: string };

		const topPlayers = <T,>(
			players: T[],
			getRawValue: (player: T) => number | null,
			formatValue: (value: number) => string,
			getDescription: (player: T, rawValue: number) => string,
			higherIsBetter = true
		): RankedPlayer[] => {
			return players
				.map(player => {
					const rawValue = getRawValue(player);
					if (rawValue === null || Number.isNaN(rawValue)) {
						return null;
					}

					const name = formatPlayerName((player as { Name?: string }).Name);
					if (name === "N/A") {
						return null;
					}

					return {
						player: name,
						value: formatValue(rawValue),
						raw: rawValue,
						description: getDescription(player, rawValue)
					};
				})
				.filter((entry): entry is RankedPlayer => entry !== null)
				.sort((a, b) => (higherIsBetter ? b.raw - a.raw : a.raw - b.raw))
				.slice(0, 15);
		};

		const toCard = (
			key: MetricKey,
			title: string,
			accent: string,
			group: "batting" | "bowling" | "fielding",
			ranked: RankedPlayer[]
		): LeaderCard => ({
			key,
			title,
			player: ranked[0]?.player || "N/A",
			value: ranked[0]?.value || "N/A",
			description: ranked[0]?.description || "No additional context available",
			secondary: ranked.slice(1).map((entry, index) => ({
				rank: index + 2,
				player: entry.player,
				value: entry.value,
				description: entry.description
			})),
			accent,
			group
		});

		const topRuns = topPlayers(
			batStats,
			player => player.Statistics.BattingAggregate,
			value => `${value} runs`,
			player => `from ${player.Statistics.BattingInnings} innings`
		);
		const topAverage = topPlayers(
			batStats,
			player => toBattingAverage(player),
			value => `${formatDecimal(value)} avg`,
			player =>
				`${player.Statistics.BattingAggregate} runs from ${player.Statistics.BattingInnings} innings (${player.Statistics.BattingNotOuts} not outs)`
		);
		const mostFours = topPlayers(
			batStats,
			player => player.Statistics.BattingFours,
			value => `${value} fours`,
			player => `from ${player.Statistics.BattingInnings} innings`
		);
		const mostBallsFaced = topPlayers(
			batStats,
			player => player.Statistics.BattingBallsFaced,
			value => `${value} balls`,
			player => `across ${player.Statistics.BattingInnings} innings`
		);
		const topWickets = topPlayers(
			bowlStats,
			player => player.Statistics.BowlingWickets,
			value => `${value} wickets`,
			player => `from ${player.Statistics.Matches} matches`
		);
		const bestEconomy = topPlayers(
			bowlStats,
			player => toEconomy(player),
			value => `${formatDecimal(value)} econ`,
			player =>
				`${player.Statistics.BowlingRuns} runs conceded in ${player.Statistics.BowlingBalls} balls`,
			false
		);
		const mostSixes = topPlayers(
			batStats,
			player => player.Statistics.BattingSixes,
			value => `${value} sixes`,
			player => `from ${player.Statistics.BattingInnings} innings`
		);
		const mostFifties = topPlayers(
			batStats,
			player => player.Statistics.Batting50s,
			value => `${value} fifties`,
			player => `from ${player.Statistics.BattingInnings} innings`
		);
		const mostHundreds = topPlayers(
			batStats,
			player => player.Statistics.Batting100s,
			value => `${value} hundreds`,
			player => `from ${player.Statistics.BattingInnings} innings`
		);
		const bestStrikeRate = topPlayers(
			batStats,
			player => toStrikeRate(player),
			value => `${formatDecimal(value)} SR`,
			player =>
				`${player.Statistics.BattingAggregate} runs from ${player.Statistics.BattingBallsFaced} balls`
		);
		const bestBoundaryRate = topPlayers(
			batStats,
			player => toBoundaryRate(player),
			value => `${formatDecimal(value)} balls/boundary`,
			player => {
				const boundaries = player.Statistics.BattingFours + player.Statistics.BattingSixes;
				return `${boundaries} boundaries from ${player.Statistics.BattingBallsFaced} balls`;
			},
			false
		);
		const bestBoundaryPercentage = topPlayers(
			batStats,
			player => toBoundaryPercentage(player),
			value => `${formatDecimal(value)} %`,
			player => {
				const boundaryRuns =
					player.Statistics.BattingFours * 4 + player.Statistics.BattingSixes * 6;
				return `${boundaryRuns} boundary runs from ${player.Statistics.BattingAggregate} total runs`;
			}
		);
		const bestBowlAverage = topPlayers(
			bowlStats,
			player => toBowlingAverage(player),
			value => `${formatDecimal(value)} avg`,
			player =>
				`${player.Statistics.BowlingRuns} runs conceded for ${player.Statistics.BowlingWickets} wickets`,
			false
		);
		const bestBowlingStrikeRate = topPlayers(
			bowlStats,
			player => toBowlingStrikeRate(player),
			value => `${formatDecimal(value)} SR`,
			player =>
				`${player.Statistics.BowlingBalls} balls bowled for ${player.Statistics.BowlingWickets} wickets`,
			false
		);
		const mostCatches = topPlayers(
			fieldStats,
			player => player.Statistics.FieldingTotalCatches,
			value => `${value} catches`,
			player => `from ${player.Statistics.Matches} matches`
		);
		const mostRunOuts = topPlayers(
			fieldStats,
			player => player.Statistics.FieldingRunOuts,
			value => `${value} run outs`,
			player => `from ${player.Statistics.Matches} matches`
		);
		const mostStumpings = topPlayers(
			fieldStats,
			player => player.Statistics.FieldingStumpings,
			value => `${value} stumpings`,
			player => `from ${player.Statistics.Matches} matches`
		);
		const mostGamesPlayed = topPlayers(
			fieldStats,
			player => player.Statistics.Matches,
			value => `${value} matches`,
			() => `-`
		);
		const mostMaidens = topPlayers(
			bowlStats,
			player => player.Statistics.BowlingMaidens,
			value => `${value} maidens`,
			player => `from ${formatBallsAsOvers(player.Statistics.BowlingBalls)} overs bowled`
		);
		const mostBallsBowled = topPlayers(
			bowlStats,
			player => player.Statistics.BowlingBalls,
			value => `${value} balls`,
			player => `in ${player.Statistics.Matches} matches`
		);

		return [
			// batting
			toCard("mostRuns", "Most Runs", "text-emerald-300", "batting", topRuns),
			toCard("mostBallsFaced", "Most Balls Faced", "text-orange-300", "batting", mostBallsFaced),
			toCard("topAverage", "Top Average", "text-cyan-300", "batting", topAverage),
			toCard("mostFours", "Most Fours", "text-yellow-300", "batting", mostFours),
			toCard("mostSixes", "Most Sixes", "text-amber-300", "batting", mostSixes),
			toCard("mostFifties", "Most Fifties", "text-lime-300", "batting", mostFifties),
			toCard("mostHundreds", "Most Hundreds", "text-violet-300", "batting", mostHundreds),
			toCard("bestStrikeRate", "Best Strike Rate", "text-sky-300", "batting", bestStrikeRate),
			toCard(
				"bestBoundaryRate",
				"Best Boundary Rate",
				"text-green-300",
				"batting",
				bestBoundaryRate
			),
			toCard(
				"bestBoundaryPercentage",
				"Boundary Percentage",
				"text-emerald-300",
				"batting",
				bestBoundaryPercentage
			),

			// bowling
			toCard("mostWickets", "Most Wickets", "text-orange-300", "bowling", topWickets),
			toCard("mostBallsBowled", "Most Balls Bowled", "text-cyan-300", "bowling", mostBallsBowled),
			toCard("bestEconomy", "Best Economy", "text-fuchsia-300", "bowling", bestEconomy),
			toCard(
				"bestBowlAverage",
				"Best Bowling Average",
				"text-rose-300",
				"bowling",
				bestBowlAverage
			),
			toCard(
				"bestBowlingStrikeRate",
				"Best Bowling Strike Rate",
				"text-cyan-300",
				"bowling",
				bestBowlingStrikeRate
			),
			toCard("mostMaidens", "Most Maidens", "text-indigo-300", "bowling", mostMaidens),
			// fielding
			toCard("mostGamesPlayed", "Most Games Played", "text-sky-300", "fielding", mostGamesPlayed),
			toCard("mostCatches", "Most Catches", "text-teal-300", "fielding", mostCatches),
			toCard("mostRunOuts", "Most Run Outs", "text-emerald-300", "fielding", mostRunOuts),
			toCard("mostStumpings", "Most Stumpings", "text-lime-300", "fielding", mostStumpings)
		];
	}, [batStats, bowlStats, fieldStats]);

	const battingCards = allCards.filter(card => card.group === "batting");
	const bowlingCards = allCards.filter(card => card.group === "bowling");
	const fieldingCards = allCards.filter(card => card.group === "fielding");

	if (loading) {
		return (
			<div className="min-h-[380px] flex items-center justify-center">
				<div className="flex flex-col items-center gap-3 text-slate-300">
					<Loader color="teal" size="lg" />
					<p>Loading dashboard highlights...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return <div className="p-6 text-red-300">{error}</div>;
	}

	return (
		<div className="p-5 md:p-7">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
				<Select
					label="Season"
					variant="filled"
					placeholder="Season"
					data={seasons.map(season => ({ value: season.Id, label: season.Name }))}
					value={selectedSeason}
					onChange={season => {
						const nextSeason = season || "";
						setSelectedSeason(nextSeason);
						setSelectedGrade("all");
						updateDashboardQuery(nextSeason, "all");
					}}
					allowDeselect={false}
					classNames={selectClassNames}
				/>

				<Select
					label="Grade"
					variant="filled"
					placeholder="Grade"
					data={[
						{ value: "all", label: "All" },
						...grades.map(grade => ({
							value: grade.Id,
							label: `${grade.Grade.Name} (${grade.Name})`
						}))
					]}
					value={selectedGrade}
					onChange={grade => {
						const nextGrade = grade || "all";
						setSelectedGrade(nextGrade);
						updateDashboardQuery(selectedSeason, nextGrade);
					}}
					allowDeselect={false}
					classNames={selectClassNames}
				/>
			</div>

			<div className="mb-2 rounded-2xl border border-slate-700 bg-slate-950/35 p-4 md:p-5">
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
					<div>
						<p className="text-xs uppercase tracking-[0.14em] text-slate-400">
							Season Leaders Dashboard
						</p>
						<h3 className="text-2xl text-white">Impact Highlights</h3>
					</div>
					<div className="flex items-center gap-2 text-xs">
						<span className="rounded-full border border-slate-600 bg-slate-900/70 px-3 py-1 text-slate-200">
							{seasonName}
						</span>
						<span className="rounded-full border border-slate-600 bg-slate-900/70 px-3 py-1 text-slate-200">
							{gradeName}
						</span>
					</div>
				</div>

				<div className="space-y-5">
					{[
						{ title: "Batting", cards: battingCards },
						{ title: "Bowling", cards: bowlingCards },
						{ title: "Fielding", cards: fieldingCards }
					].map(section =>
						section.cards.length ? (
							<div key={section.title}>
								<h4 className="text-sm uppercase tracking-[0.12em] text-slate-300 mb-3">
									{section.title}
								</h4>
								<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
									{section.cards.map(card =>
										(() => {
											const isExpanded = Boolean(expandedCards[card.key]);
											const defaultVisibleCount = 3;
											const alwaysVisibleSecondary = card.secondary.slice(0, defaultVisibleCount);
											const expandableSecondary = card.secondary.slice(defaultVisibleCount);
											const hiddenCount = Math.max(card.secondary.length - defaultVisibleCount, 0);

											return (
												<div
													key={card.key}
													className="rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3"
												>
													<p className="text-xs uppercase tracking-[0.12em] text-slate-400">
														{card.title}
													</p>
													<div className="mt-1 flex items-start justify-between gap-3">
														<div className="min-w-0">
															<p className="text-lg text-white truncate">{card.player}</p>
															{/* <p className={`text-sm ${card.accent}`}>{card.value}</p> */}
															<p className="text-xs text-slate-400 mt-1">{card.description}</p>
														</div>
														<div className="shrink-0 text-right">
															<p className={`text-5xl leading-none font-semibold ${card.accent}`}>
																{splitStatValue(card.value).primary}
															</p>
															{splitStatValue(card.value).suffix ? (
																<p className="text-[10px] uppercase tracking-[0.1em] text-slate-400 mt-1">
																	{splitStatValue(card.value).suffix}
																</p>
															) : null}
														</div>
													</div>
													{card.secondary.length > 0 && (
														<div className="mt-2 pt-2 border-t border-slate-700/70 space-y-1">
															{alwaysVisibleSecondary.map(secondaryEntry => (
																<div
																	key={`${card.key}-${secondaryEntry.rank}-${secondaryEntry.player}`}
																	className="text-xs"
																>
																	<div className="flex items-center justify-between gap-2">
																		<p className="text-slate-400 truncate">
																			{secondaryEntry.rank}. {secondaryEntry.player}
																		</p>
																		<p className={`${card.accent} shrink-0`}>
																			{secondaryEntry.value}
																		</p>
																	</div>
																	<p className="text-slate-500 truncate">
																		{secondaryEntry.description}
																	</p>
																</div>
															))}
															<div
																className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
																	isExpanded ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
																}`}
															>
																<div className="space-y-1 pt-1">
																	{expandableSecondary.map(secondaryEntry => (
																		<div
																			key={`${card.key}-${secondaryEntry.rank}-${secondaryEntry.player}`}
																			className="text-xs"
																		>
																			<div className="flex items-center justify-between gap-2">
																				<p className="text-slate-400 truncate">
																					{secondaryEntry.rank}. {secondaryEntry.player}
																				</p>
																				<p className={`${card.accent} shrink-0`}>
																					{secondaryEntry.value}
																				</p>
																			</div>
																			<p className="text-slate-500 truncate">
																				{secondaryEntry.description}
																			</p>
																		</div>
																	))}
																</div>
															</div>
															{card.secondary.length > defaultVisibleCount && (
																<button
																	type="button"
																	onClick={() => toggleCardExpanded(card.key)}
																	className="pt-1 text-[11px] uppercase tracking-[0.1em] text-slate-300 hover:text-white"
																>
																	{isExpanded ? "Show Less" : `Show ${hiddenCount} More ↓ `}
																</button>
															)}
														</div>
													)}
												</div>
											);
										})()
									)}
								</div>
							</div>
						) : null
					)}
				</div>
			</div>
		</div>
	);
};

export default SeasonDashboard;
